import { api, request, requireAccount, withAccount, API_BASE } from "./core";

// ObservationNote is the D-06 dark-matter prose slot (ADR, design decisions,
// "which projects were running") that git can't capture. The BE returns the
// row plus the resolved team_roles and a per-viewer can_edit flag (the
// visibility filter — private/team/galaxy — is applied server-side).
export interface ObservationNote {
  id: number;
  galaxy_id: number;
  git_sha?: string;
  observation_kind: string;
  authored_by: string; // human | connector
  author_user_id?: number;
  target_user_id?: number;
  visibility: string; // private | team | galaxy
  source_kind: string; // manual | slack | meeting | document | conversation | other
  note_text: string;
  written_at: string; // RFC3339
  period_start?: string; // YYYY-MM (human timeline placement; range start)
  period_end?: string;   // YYYY-MM (range end; equals start for single-month)
  team_roles: string[];
  can_edit: boolean;
}

// observationNoteApi drives the manual authoring path only. Connectors
// (Passive/Interactive) land notes server-side and surface through `list`.
export const observationNoteApi = {
  list: (accountLogin: string, opts?: { targetUserId?: number; gitSha?: string }) => {
    const extra: Record<string, string> = {};
    if (opts?.targetUserId != null) extra.target_user_id = String(opts.targetUserId);
    if (opts?.gitSha) extra.git_sha = opts.gitSha;
    return api.get<ObservationNote[]>(withAccount("/observation-notes", requireAccount(accountLogin, "observationNoteApi.list"), extra));
  },
  create: (accountLogin: string, data: { note_text: string; visibility?: string; target_user_id?: number; git_sha?: string; period_start?: string; period_end?: string; team_roles?: string[] }) =>
    api.post<ObservationNote>(withAccount("/observation-notes", requireAccount(accountLogin, "observationNoteApi.create")), data),
  update: (accountLogin: string, data: { id: number; note_text: string; visibility?: string; target_user_id?: number; git_sha?: string; period_start?: string; period_end?: string; team_roles?: string[] }) =>
    api.put<ObservationNote>(withAccount("/observation-notes", requireAccount(accountLogin, "observationNoteApi.update")), data),
  delete: (accountLogin: string, id: number) =>
    request<void>(withAccount("/observation-notes", requireAccount(accountLogin, "observationNoteApi.delete")), { method: "DELETE", body: JSON.stringify({ id }) }),
};

// ADR (Architecture Decision Record) — a structured dark-matter entity, sibling
// of ObservationNote (project_adr_darkmatter). D-06 forbids a category column on
// notes, so an ADR is its own entity, not a note tag. The chronicle lane renders
// notes and ADRs together, coloured by entity type. Point-in-time (decided_month);
// ADRs do not span a range.
export interface ADR {
  id: number;
  galaxy_id: number;
  title: string;
  context?: string;
  criteria?: string;
  options?: string;
  decision: string;
  consequences?: string;
  status: string; // proposed | accepted | rejected | superseded
  superseded_by_id?: number;
  decided_month?: string; // YYYY-MM (point-in-time placement)
  git_sha?: string;
  visibility: string; // private | galaxy
  author_user_id?: number;
  written_at: string; // RFC3339
  can_edit: boolean;
}

type ADRWrite = {
  title: string;
  decision: string;
  context?: string;
  criteria?: string;
  options?: string;
  consequences?: string;
  status?: string;
  decided_month?: string;
  git_sha?: string;
  visibility?: string;
};

export const adrApi = {
  list: (accountLogin: string) =>
    api.get<ADR[]>(withAccount("/adrs", requireAccount(accountLogin, "adrApi.list"))),
  create: (accountLogin: string, data: ADRWrite) =>
    api.post<ADR>(withAccount("/adrs", requireAccount(accountLogin, "adrApi.create")), data),
  update: (accountLogin: string, data: ADRWrite & { id: number }) =>
    api.put<ADR>(withAccount("/adrs", requireAccount(accountLogin, "adrApi.update")), data),
  delete: (accountLogin: string, id: number) =>
    request<void>(withAccount("/adrs", requireAccount(accountLogin, "adrApi.delete")), { method: "DELETE", body: JSON.stringify({ id }) }),
};

// ChronicleQuota — the galaxy's chronicle free-tier status. A free galaxy can
// create up to `limit` entries (notes + ADRs + manual member events); past that
// it needs a paid plan (Nova). Reads are never gated, but a free galaxy's
// display is capped to the latest `limit` entries.
export interface ChronicleQuota {
  paid: boolean;
  used: number;
  limit: number; // 0 = unlimited (paid)
  can_create: boolean;
}

export const chronicleApi = {
  quota: (accountLogin: string) =>
    api.get<ChronicleQuota>(withAccount("/chronicle/quota", requireAccount(accountLogin, "chronicleApi.quota"))),
};

// SlackConnection mirrors the BE's galaxy-scoped status DTO. It carries NO
// secret — the bot token never leaves the server (D-04 / passive-connector §4).
// team_name / trigger_emoji are null when nothing is connected.
export interface SlackConnection {
  connected: boolean;
  team_name: string | null;
  trigger_emoji: string | null;
  // Weekly-digest settings the status echoes so the settings screen can render
  // the current destination + on/off state on load (docs/ace/slack-digest.md).
  // These are NON-secret config; the bot token is still never returned. null
  // channel = unset.
  digest_channel_id?: string | null;
  digest_enabled?: boolean;
  // WHEN the weekly digest posts: local weekday (Go time.Weekday, Sunday=0..
  // Saturday=6), local hour 0..23, and the IANA timezone they're read in. The
  // cadence stays weekly; these only seed the day/hour/timezone selects on load.
  digest_day_of_week?: number;
  digest_hour?: number;
  digest_timezone?: string;
}

// SlackChannel is one row of the digest channel picker — id (to save) + name (to
// label). No secret. available=false means the BE couldn't list channels (the App
// lacks channels:read / groups:read, or it's not connected); the FE then falls
// back to manual channel-id entry.
export interface SlackChannel {
  id: string;
  name: string;
}
export interface SlackDigestChannels {
  channels: SlackChannel[];
  available: boolean;
}

// slackIntegrationApi — the Passive Connector settings surface
// (docs/ace/passive-connector.md). All three calls are galaxy-scoped via
// withAccount; the BE resolves the galaxy from the authenticated scope only, so
// a caller can never read/connect/disconnect another galaxy's workspace (Galaxy
// Boundary). authorizeUrl is fetched WITH the JWT and the caller then navigates
// the browser itself — a top-level redirect can't carry the auth header.
export const slackIntegrationApi = {
  get: (accountLogin: string) =>
    api.get<SlackConnection>(withAccount("/integrations/slack", requireAccount(accountLogin, "slackIntegrationApi.get"))),
  authorizeUrl: (accountLogin: string) =>
    api.get<{ url: string }>(
      withAccount("/integrations/slack/authorize-url", requireAccount(accountLogin, "slackIntegrationApi.authorizeUrl")),
    ),
  // disconnect drops the galaxy's workspace mapping. Cache scope to invalidate
  // on success: only this galaxy's Slack connection (the caller re-fetches via
  // .get) — no other galaxy-scoped cache is touched, since disconnect removes
  // nothing but the slack_integrations row.
  disconnect: (accountLogin: string) =>
    api.delete<void>(withAccount("/integrations/slack", requireAccount(accountLogin, "slackIntegrationApi.disconnect"))),
  // digestPreview posts THIS galaxy's current observation as a Slack Digest to
  // its channel, right now (docs/ace/slack-digest.md). channelId, when given, is
  // also saved as the galaxy's digest destination (first-time setup: type a
  // channel + preview in one step). The BE resolves the galaxy + token from the
  // authenticated scope only — never from this body — so the digest can only ever
  // reach THIS galaxy's workspace (Galaxy Boundary). The automatic weekly post is
  // a separate (PR-B) feature.
  digestPreview: (accountLogin: string, channelId?: string) =>
    api.post<{ status: string }>(
      withAccount("/integrations/slack/digest/preview", requireAccount(accountLogin, "slackIntegrationApi.digestPreview")),
      channelId ? { channel_id: channelId } : {},
    ),
  // digestChannels lists the channels THIS galaxy's bot can post to, for the
  // settings picker (docs/ace/slack-digest.md). The BE resolves the bot token
  // from the authenticated scope only — never the request — so a caller can only
  // ever list its OWN workspace's channels (Galaxy Boundary). available=false
  // (with an empty list) signals the FE to fall back to manual channel-id entry.
  digestChannels: (accountLogin: string) =>
    api.get<SlackDigestChannels>(
      withAccount("/integrations/slack/digest/channels", requireAccount(accountLogin, "slackIntegrationApi.digestChannels")),
    ),
  // saveDigest persists the galaxy's weekly-digest destination + on/off flag + the
  // posting schedule (local weekday / hour / IANA timezone). The cadence stays
  // weekly; the schedule only moves WHEN in the week it lands. The BE keys the
  // write on the authenticated galaxy_id only (Galaxy Boundary), validates the
  // schedule (rejects out-of-range day/hour or an unloadable tz with code
  // "digest_schedule_invalid"), and rejects enabling with no channel
  // ("digest_channel_unset"). On success the caller re-fetches .get().
  saveDigest: (
    accountLogin: string,
    channelId: string,
    enabled: boolean,
    dayOfWeek: number,
    hour: number,
    timezone: string,
  ) =>
    api.put<{ status: string }>(
      withAccount("/integrations/slack/digest", requireAccount(accountLogin, "slackIntegrationApi.saveDigest")),
      { channel_id: channelId, enabled, day_of_week: dayOfWeek, hour, timezone },
    ),
};

// configApi — galaxy-scoped (BE writes to states_account_config keyed
// by account_id resolved from account_login).
export const configApi = {
  get: <T = unknown>(accountLogin: string) =>
    api.get<T>(withAccount("/config", requireAccount(accountLogin, "configApi.get"))),
  put: (accountLogin: string, body: unknown) =>
    api.put<unknown>(withAccount("/config", requireAccount(accountLogin, "configApi.put")), body),
  uploadYaml: async (file: File, accountLogin: string, mode: "replace" | "merge" = "replace"): Promise<unknown> => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("file", file);
    const url = `${API_BASE}/config/upload-yaml?account_login=${encodeURIComponent(requireAccount(accountLogin, "configApi.uploadYaml"))}&mode=${mode}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.message || res.statusText);
    }
    return res.json();
  },
  downloadYaml: (accountLogin: string) => {
    const token = localStorage.getItem("token");
    const acc = requireAccount(accountLogin, "configApi.downloadYaml");
    window.open(`${API_BASE}/config/download-yaml?account_login=${encodeURIComponent(acc)}&token=${token}`, "_blank");
  },
};
